import { Table, SendMessageTooltip, EmptyContent, HeadItemType, Icon } from 'components'
import { formatDate, isDateAfter } from 'commons/utils/date'
import { MetaResultRegion, MetaResultTeam } from 'services/home/interfaces'
import { useSelector, useDispatch } from 'store/store'
import { OfficeTypes } from '../GoalProgress'
import { officeColors } from '../GoalProgress/constants'
import { numberToDecimalString as toDecimal } from 'commons/utils/number'

import * as S from './styles'
import { getGeneralMetaItem, setGeneralMetaData } from 'store/HomeContext/homeSlice'

interface GeneralTableProps {
  quota: string
  selectedOffice: OfficeTypes
  screenshotComponentRef: React.MutableRefObject<null>
  isPrinting: boolean
}

const GeneralTable: React.FC<GeneralTableProps> = ({
  quota,
  selectedOffice,
  screenshotComponentRef,
  isPrinting
}) => {
  const dispatch = useDispatch()
  const {
    loadingRanking: loading,
    generalMetaData,
    loadingTableItem
  } = useSelector((state) => state.home)

  const getZoneUser = (item: MetaResultRegion) => {
    if (item.NomeUsuarioZona && item.EmailUsuarioZona) {
      return (
        <SendMessageTooltip
          name={item.NomeUsuarioZona}
          email={item.EmailUsuarioZona.split(';')[0]}
          phone={item.TelefoneUsuarioZona}
        >
          <span>{item.CodigoUsuarioZona ?? item.DescricaoZona}</span>
        </SendMessageTooltip>
      )
    }
    return <span>{item.DescricaoZona}</span>
  }

  const tableLabels = (): HeadItemType[] => {
    const tableHead: HeadItemType[] = [
      { label: 'ID', columnName: 'ID' },
      { label: 'Equipes de vendas', columnName: 'EquipesVenda' },
      { label: 'Meta total', columnName: 'MetaTotal' },
      { label: 'Vol. aprovado', columnName: 'VolumeAprovado' }
    ]

    if (generalMetaData.length) {
      generalMetaData[0].Dias.forEach((item) => {
        const newItem = formatDate(item.Dia, 'DD/MM')
        tableHead.push({ label: newItem, columnName: newItem })
      })
    }

    return tableHead
  }

  const tableData = () => {
    return generalMetaData.map((item) => {
      const rowContent = [
        <strong key={`title-${item.Equipe}`}>{item.Equipe}</strong>,
        <strong key={`desc-${item.Equipe}`}>{item.DescricaoEquipe}</strong>,
        <S.DataWrap key={`meta-${item.Equipe}`}>
          <Icon icon="sandal" color={officeColors[selectedOffice]} size="16" />
          <strong>{toDecimal(item.Meta)}</strong>
        </S.DataWrap>,
        <S.DataWrap key={`approved-${item.Equipe}`} achieved={item.PercentualMeta >= 100}>
          <Icon icon="sandal" color={officeColors[selectedOffice]} size="16" />
          <strong>{toDecimal(item.VolumeAprovado)}</strong>
        </S.DataWrap>
      ]

      item.Dias.forEach((item) => {
        const isFutureDate = isDateAfter(item.Dia, new Date())
        rowContent.push(
          <S.DataWrap
            className={item.PercentualMeta >= 100 ? 'table-cell-success-bg' : undefined}
            key={`percent-${item.Dia}`}
            achieved={item.PercentualMeta >= 100}
          >
            <strong>
              {isFutureDate ? '-' : `${Number(item.PercentualMeta.toFixed(2))} %`}
            </strong>
          </S.DataWrap>
        )
      })

      return {
        id: item.Equipe,
        content: rowContent,
        expanded: item.expanded ?? false,
        subRows: tableSubrows(item)
      }
    })
  }

  const tableSubrows = (item: MetaResultTeam) => {
    if (!item.regions?.length) return undefined

    return item.regions.map((region) => {
      const subrowContent = [
        region.Regiao,
        <S.DataWrap key={`tooltip-${region.Regiao}`}>{getZoneUser(region)}</S.DataWrap>,
        <S.DataWrap key={`meta-${region.Regiao}`}>
          <span>{toDecimal(region.Meta)}</span>
        </S.DataWrap>,
        <S.DataWrap
          key={`approved-${region.Regiao}`}
          achieved={region.PercentualMeta >= 100}
        >
          <span>{toDecimal(region.VolumeAprovado)}</span>
        </S.DataWrap>
      ]

      region.Dias.forEach((dia) => {
        const isFutureDate = isDateAfter(dia.Dia, new Date())
        subrowContent.push(
          <S.DataWrap key={`percent-${dia.Dia}`} achieved={dia.PercentualMeta >= 100}>
            <span>
              {isFutureDate ? '-' : `${Number(dia.PercentualMeta.toFixed(2))} %`}
            </span>
          </S.DataWrap>
        )
      })

      return {
        id: region.Regiao,
        content: subrowContent
      }
    })
  }

  const setExpanded = (id: string) => {
    const selectedItem = generalMetaData.find((item) => item.Equipe === id)
    const isItemExpanded = selectedItem?.expanded
    const hasLoadedRegions = selectedItem?.regions?.length

    if (isItemExpanded || hasLoadedRegions) {
      const newMetaList = generalMetaData.map((item) => {
        if (item.Equipe === id) {
          return { ...item, expanded: !item?.expanded }
        }
        return item
      })
      dispatch(setGeneralMetaData(newMetaList))
    } else {
      dispatch(
        getGeneralMetaItem(id, {
          AnoMesCota: quota,
          Escritorio: selectedOffice,
          Regiao: null,
          Equipe: id
        })
      )
    }
  }

  return (
    <>
      {!loading && !generalMetaData.length ? (
        <EmptyContent
          title="Nenhum resultado encontrado"
          description="Não foi possível encontrar dados para o período selecionado. Revise as opções selecionadas e tente novamente."
        />
      ) : (
        <Table
          headData={tableLabels()}
          bodyData={tableData()}
          rowHeight={48}
          tableRef={screenshotComponentRef}
          fixedLeftCols={[0, 1, 2, 3]}
          customFixedLeftColsWidth={[80, 180, 140, 140]}
          toggleExpand={setExpanded}
          distributeWidth={false}
          hasAsyncExpand
          isLoading={loadingTableItem}
          iconColor={officeColors[selectedOffice]}
          isPrinting={isPrinting}
        />
      )}
    </>
  )
}

export default GeneralTable
